// Number System & Approximation Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const numberSystemSamples = {
  fundamental: [
    {
      id: 'number_fund_01',
      problem: "Round 3.14159 to 2 decimal places",
      problemType: "number_system",
      difficulty: "fundamental",
      description: "Basic decimal rounding",
      graphData: {
        nodes: [
          {
            id: "decimal_number",
            name: "Decimal Number",
            type: "concepts",
            description: "A number with decimal places",
            value: "3.14159",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "rounding_rule",
            name: "Rounding Rule",
            type: "concepts",
            description: "Look at the digit after the target place",
            value: "If ≥ 5, round up; if < 5, round down",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_place",
            name: "Target Place",
            type: "concepts",
            description: "The decimal place to round to",
            value: "2 decimal places",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Rounded Result",
            type: "values",
            description: "The rounded number",
            value: "3.14",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "rounding_formula",
            name: "Rounding Formula",
            type: "formulas",
            description: "Rounding rule: if digit after target place ≥ 5, round up; if < 5, round down",
            value: "If d ≥ 5, round up; if d < 5, round down",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_target_place",
            name: "Identify Target Place",
            type: "steps",
            description: "Recognize the target decimal place",
            value: "Step 1: Target = 2 decimal places (hundredths)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "check_next_digit",
            name: "Check Next Digit",
            type: "steps",
            description: "Look at the digit after the target place",
            value: "Step 2: Next digit = 1 (which is < 5)",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_rounding_rule",
            name: "Apply Rounding Rule",
            type: "steps",
            description: "Apply the rounding rule",
            value: "Step 3: Since 1 < 5, round down",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_result",
            name: "Write Result",
            type: "steps",
            description: "Write the rounded number",
            value: "Step 4: 3.14159 → 3.14",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "decimal_number", to: "rounding_rule", type: "requires", strength: 9 },
          { from: "rounding_rule", to: "target_place", type: "applied to", strength: 9 },
          { from: "target_place", to: "result", type: "gives", strength: 10 },
          { from: "rounding_formula", to: "result", type: "gives", strength: 10 },
          { from: "identify_target_place", to: "check_next_digit", type: "enables", strength: 8 },
          { from: "check_next_digit", to: "apply_rounding_rule", type: "enables", strength: 9 },
          { from: "apply_rounding_rule", to: "write_result", type: "leads to", strength: 10 }
        ]
      }
    },
    {
      id: 'number_fund_02',
      problem: "Express 0.75 as a fraction in simplest form",
      problemType: "number_system",
      difficulty: "fundamental",
      description: "Decimal to fraction conversion",
      graphData: {
        nodes: [
          {
            id: "decimal",
            name: "Decimal",
            type: "concepts",
            description: "A decimal number",
            value: "0.75",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "fraction_conversion",
            name: "Fraction Conversion",
            type: "concepts",
            description: "Convert decimal to fraction",
            value: "0.75 = 75/100",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Reduce fraction to simplest form",
            value: "75/100 = 3/4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Simplified Fraction",
            type: "values",
            description: "The final fraction",
            value: "3/4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "decimal", to: "fraction_conversion", type: "converted to", strength: 9 },
          { from: "fraction_conversion", to: "simplification", type: "simplified to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'number_fund_03',
      problem: "Find the value of √16",
      problemType: "number_system",
      difficulty: "fundamental",
      description: "Basic square root calculation",
      graphData: {
        nodes: [
          {
            id: "square_root",
            name: "Square Root",
            type: "concepts",
            description: "A number that when squared gives the original",
            value: "√16",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perfect_square",
            name: "Perfect Square",
            type: "concepts",
            description: "A number that is the square of an integer",
            value: "16 = 4²",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Square Root Value",
            type: "values",
            description: "The square root result",
            value: "4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "square_root", to: "perfect_square", type: "identifies", strength: 9 },
          { from: "perfect_square", to: "result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'number_fund_04',
      problem: "Express 2/5 as a decimal",
      problemType: "number_system",
      difficulty: "fundamental",
      description: "Fraction to decimal conversion",
      graphData: {
        nodes: [
          {
            id: "fraction",
            name: "Fraction",
            type: "concepts",
            description: "A rational number expressed as a ratio",
            value: "2/5",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "division",
            name: "Division",
            type: "concepts",
            description: "Divide numerator by denominator",
            value: "2 ÷ 5",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Decimal Result",
            type: "values",
            description: "The decimal equivalent",
            value: "0.4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "fraction", to: "division", type: "converted by", strength: 9 },
          { from: "division", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'number_fund_05',
      problem: "Find the value of 2³",
      problemType: "number_system",
      difficulty: "fundamental",
      description: "Basic exponentiation",
      graphData: {
        nodes: [
          {
            id: "exponent",
            name: "Exponent",
            type: "concepts",
            description: "A number indicating how many times to multiply",
            value: "2³",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "multiplication",
            name: "Multiplication",
            type: "concepts",
            description: "Multiply the base by itself",
            value: "2 × 2 × 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Power Result",
            type: "values",
            description: "The calculated value",
            value: "8",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "exponent", to: "multiplication", type: "requires", strength: 9 },
          { from: "multiplication", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'number_inter_01',
      problem: "Express 0.333... as a fraction",
      problemType: "number_system",
      difficulty: "intermediate",
      description: "Repeating decimal to fraction",
      graphData: {
        nodes: [
          {
            id: "repeating_decimal",
            name: "Repeating Decimal",
            type: "concepts",
            description: "A decimal with a repeating pattern",
            value: "0.333...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "algebraic_method",
            name: "Algebraic Method",
            type: "concepts",
            description: "Use algebra to convert repeating decimal",
            value: "Let x = 0.333..., then 10x = 3.333...",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "subtraction",
            name: "Subtraction",
            type: "concepts",
            description: "Subtract to eliminate repeating part",
            value: "10x - x = 3.333... - 0.333... = 3",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Fraction Result",
            type: "values",
            description: "The fraction equivalent",
            value: "1/3",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "repeating_decimal", to: "algebraic_method", type: "converted using", strength: 9 },
          { from: "algebraic_method", to: "subtraction", type: "leads to", strength: 9 },
          { from: "subtraction", to: "result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'number_inter_02',
      problem: "Find the value of √50 to 2 decimal places",
      problemType: "number_system",
      difficulty: "intermediate",
      description: "Approximate square root calculation",
      graphData: {
        nodes: [
          {
            id: "square_root",
            name: "Square Root",
            type: "concepts",
            description: "A number that when squared gives the original",
            value: "√50",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "approximation",
            name: "Approximation",
            type: "concepts",
            description: "Find a close estimate",
            value: "√49 = 7, √64 = 8",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "concepts",
            description: "Use calculator or estimation method",
            value: "√50 ≈ 7.07",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Approximate Result",
            type: "values",
            description: "The rounded result",
            value: "7.07",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "square_root", to: "approximation", type: "requires", strength: 9 },
          { from: "approximation", to: "calculation", type: "leads to", strength: 9 },
          { from: "calculation", to: "result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'number_inter_03',
      problem: "Express 1.25 as a mixed number",
      problemType: "number_system",
      difficulty: "intermediate",
      description: "Decimal to mixed number conversion",
      graphData: {
        nodes: [
          {
            id: "decimal",
            name: "Decimal",
            type: "concepts",
            description: "A decimal number",
            value: "1.25",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "whole_part",
            name: "Whole Part",
            type: "concepts",
            description: "The integer part of the decimal",
            value: "1",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "fraction_part",
            name: "Fraction Part",
            type: "concepts",
            description: "Convert decimal part to fraction",
            value: "0.25 = 25/100 = 1/4",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Mixed Number",
            type: "values",
            description: "The mixed number result",
            value: "1 1/4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "decimal", to: "whole_part", type: "has", strength: 9 },
          { from: "decimal", to: "fraction_part", type: "has", strength: 9 },
          { from: "whole_part", to: "result", type: "combines with", strength: 9 },
          { from: "fraction_part", to: "result", type: "combines with", strength: 9 }
        ]
      }
    },
    {
      id: 'number_inter_04',
      problem: "Find the value of 2^(-3)",
      problemType: "number_system",
      difficulty: "intermediate",
      description: "Negative exponent calculation",
      graphData: {
        nodes: [
          {
            id: "negative_exponent",
            name: "Negative Exponent",
            type: "concepts",
            description: "An exponent that is negative",
            value: "2^(-3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "reciprocal_rule",
            name: "Reciprocal Rule",
            type: "concepts",
            description: "a^(-n) = 1/(a^n)",
            value: "2^(-3) = 1/(2^3)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "concepts",
            description: "Calculate the positive exponent first",
            value: "2^3 = 8, so 1/8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Final Result",
            type: "values",
            description: "The calculated value",
            value: "1/8",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "negative_exponent", to: "reciprocal_rule", type: "converted using", strength: 9 },
          { from: "reciprocal_rule", to: "calculation", type: "leads to", strength: 9 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'number_inter_05',
      problem: "Express 0.125 as a fraction in simplest form",
      problemType: "number_system",
      difficulty: "intermediate",
      description: "Decimal to fraction with simplification",
      graphData: {
        nodes: [
          {
            id: "decimal",
            name: "Decimal",
            type: "concepts",
            description: "A decimal number",
            value: "0.125",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "fraction_conversion",
            name: "Fraction Conversion",
            type: "concepts",
            description: "Convert decimal to fraction",
            value: "0.125 = 125/1000",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "concepts",
            description: "Reduce fraction to simplest form",
            value: "125/1000 = 1/8",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Simplified Fraction",
            type: "values",
            description: "The final fraction",
            value: "1/8",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "decimal", to: "fraction_conversion", type: "converted to", strength: 9 },
          { from: "fraction_conversion", to: "simplification", type: "simplified to", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'number_hard_01',
      problem: "Express 0.142857... as a fraction",
      problemType: "number_system",
      difficulty: "harder",
      description: "Complex repeating decimal to fraction",
      graphData: {
        nodes: [
          {
            id: "repeating_decimal",
            name: "Repeating Decimal",
            type: "concepts",
            description: "A decimal with a repeating pattern",
            value: "0.142857...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "algebraic_method",
            name: "Algebraic Method",
            type: "concepts",
            description: "Use algebra to convert repeating decimal",
            value: "Let x = 0.142857..., then 1000000x = 142857.142857...",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "subtraction",
            name: "Subtraction",
            type: "concepts",
            description: "Subtract to eliminate repeating part",
            value: "1000000x - x = 142857",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Fraction Result",
            type: "values",
            description: "The fraction equivalent",
            value: "1/7",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "repeating_decimal", to: "algebraic_method", type: "converted using", strength: 9 },
          { from: "algebraic_method", to: "subtraction", type: "leads to", strength: 9 },
          { from: "subtraction", to: "result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'number_hard_02',
      problem: "Find the value of √(2 + √3) to 3 decimal places",
      problemType: "number_system",
      difficulty: "harder",
      description: "Nested square root approximation",
      graphData: {
        nodes: [
          {
            id: "nested_square_root",
            name: "Nested Square Root",
            type: "concepts",
            description: "A square root containing another square root",
            value: "√(2 + √3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inner_calculation",
            name: "Inner Calculation",
            type: "concepts",
            description: "Calculate the inner square root first",
            value: "√3 ≈ 1.732",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "addition",
            name: "Addition",
            type: "concepts",
            description: "Add the result to the constant",
            value: "2 + 1.732 = 3.732",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "final_square_root",
            name: "Final Square Root",
            type: "concepts",
            description: "Calculate the outer square root",
            value: "√3.732 ≈ 1.932",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Approximate Result",
            type: "values",
            description: "The final rounded result",
            value: "1.932",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "nested_square_root", to: "inner_calculation", type: "requires", strength: 9 },
          { from: "inner_calculation", to: "addition", type: "leads to", strength: 9 },
          { from: "addition", to: "final_square_root", type: "leads to", strength: 9 },
          { from: "final_square_root", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'number_hard_03',
      problem: "Express 0.999... as a fraction",
      problemType: "number_system",
      difficulty: "harder",
      description: "Repeating decimal that equals 1",
      graphData: {
        nodes: [
          {
            id: "repeating_decimal",
            name: "Repeating Decimal",
            type: "concepts",
            description: "A decimal with a repeating pattern",
            value: "0.999...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "algebraic_method",
            name: "Algebraic Method",
            type: "concepts",
            description: "Use algebra to convert repeating decimal",
            value: "Let x = 0.999..., then 10x = 9.999...",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "subtraction",
            name: "Subtraction",
            type: "concepts",
            description: "Subtract to eliminate repeating part",
            value: "10x - x = 9.999... - 0.999... = 9",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Fraction Result",
            type: "values",
            description: "The fraction equivalent",
            value: "1",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "repeating_decimal", to: "algebraic_method", type: "converted using", strength: 9 },
          { from: "algebraic_method", to: "subtraction", type: "leads to", strength: 9 },
          { from: "subtraction", to: "result", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'number_hard_04',
      problem: "Find the value of (1 + √5)/2 to 4 decimal places",
      problemType: "number_system",
      difficulty: "harder",
      description: "Golden ratio calculation",
      graphData: {
        nodes: [
          {
            id: "golden_ratio",
            name: "Golden Ratio",
            type: "concepts",
            description: "A special mathematical constant",
            value: "(1 + √5)/2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "square_root",
            name: "Square Root",
            type: "concepts",
            description: "Calculate the square root",
            value: "√5 ≈ 2.236",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "addition",
            name: "Addition",
            type: "concepts",
            description: "Add 1 to the square root",
            value: "1 + 2.236 = 3.236",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "division",
            name: "Division",
            type: "concepts",
            description: "Divide by 2",
            value: "3.236 ÷ 2 = 1.618",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Golden Ratio Value",
            type: "values",
            description: "The calculated value",
            value: "1.6180",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "golden_ratio", to: "square_root", type: "requires", strength: 9 },
          { from: "square_root", to: "addition", type: "leads to", strength: 9 },
          { from: "addition", to: "division", type: "leads to", strength: 9 },
          { from: "division", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'number_hard_05',
      problem: "Express 0.123456789... as a fraction",
      problemType: "number_system",
      difficulty: "harder",
      description: "Complex non-repeating decimal approximation",
      graphData: {
        nodes: [
          {
            id: "non_repeating_decimal",
            name: "Non-Repeating Decimal",
            type: "concepts",
            description: "A decimal that does not repeat",
            value: "0.123456789...",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "approximation",
            name: "Approximation",
            type: "concepts",
            description: "Approximate to a finite number of places",
            value: "0.123456789",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "fraction_conversion",
            name: "Fraction Conversion",
            type: "concepts",
            description: "Convert to fraction",
            value: "123456789/1000000000",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "result",
            name: "Approximate Fraction",
            type: "values",
            description: "The fraction approximation",
            value: "123456789/1000000000",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "non_repeating_decimal", to: "approximation", type: "approximated as", strength: 9 },
          { from: "approximation", to: "fraction_conversion", type: "converted to", strength: 9 },
          { from: "fraction_conversion", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 